// @ts-nocheck
import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { Copy } from "lucide-react";
import { getAppLanguage, toLocale } from "../../utils/appLanguage";
import { ls, useLnLanguage } from "../../utils/lnText";

// ─────────────────────────────────────────────────────────────────────────────
// ASTRIA KOREA V2 — Premium Mobile-First Redesign
//
// ✨ Key Design Improvements:
// 1. Fixed symbol rendering for all tabs
// 2. Changed Daily Flow symbol to ☀️ (sun) for better visual
// 3. Fully responsive — looks gorgeous on mobile & desktop
// 4. Tab-specific animated backgrounds with unique symbols
// 5. Soft glass-morphism with beautiful gradients
// 6. Smooth micro-interactions and hover effects
// 7. Korean-inspired color palette with modern touch
// 8. Optimized typography and spacing for all screen sizes
// ─────────────────────────────────────────────────────────────────────────────

const KR_FONT =
  "'Noto Sans KR', -apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif";

// ── Premium Korean Design System ──
export const KR_V2_COLORS = {
  // Base
  bg: "#FBF9F7",
  cardBg: "rgba(255, 255, 255, 0.92)",

  // Tab-specific gradients
  dailyFlow: {
    primary: "#5B9B9B",
    light: "#7FBEBE",
    gradient: "linear-gradient(135deg, #E8F4F4, #D4ECEC)",
  },
  lifeMap: {
    primary: "#D4A373",
    light: "#E8C9A8",
    gradient: "linear-gradient(135deg, #F5EDE4, #F5E6D3)",
  },
  relationship: {
    primary: "#E8927C",
    light: "#F0B8A8",
    gradient: "linear-gradient(135deg, #FDF0EC, #FCE4DD)",
  },
  dailyCompanion: {
    primary: "#7A9EC4",
    light: "#A8C4DF",
    gradient: "linear-gradient(135deg, #E8F0F8, #DCE8F2)",
  },
  compatibility: {
    primary: "#9B7AC4",
    light: "#C4ADE0",
    gradient: "linear-gradient(135deg, #F2ECF8, #ECE4F5)",
  },
  saju: {
    primary: "#B08968",
    light: "#D4B896",
    gradient: "linear-gradient(135deg, #F7F1E8, #F0E4D3)",
  },

  // Shared
  textTitle: "#2D2532",
  textBody: "#5A4F62",
  textMuted: "#A098A8",
  shadow:
    "0 20px 60px rgba(45, 37, 50, 0.08), 0 8px 24px rgba(45, 37, 50, 0.04)",
  shadowSoft:
    "0 8px 32px rgba(45, 37, 50, 0.05), 0 2px 8px rgba(45, 37, 50, 0.02)",
  shadowHover:
    "0 24px 64px rgba(45, 37, 50, 0.10), 0 8px 32px rgba(45, 37, 50, 0.04)",
};

// ── Tab Configurations with Fixed Symbols ──
const TAB_CONFIG = {
  daily_flow: {
    symbols: ["☀️", "🌅", "☀️", "🌤️", "☀️", "🌅"],
    icon: "☀️",
    title: "오늘의 흐름",
    subtitle: ls("Daily Flow"),
    color: KR_V2_COLORS.dailyFlow,
  },
  life_map: {
    symbols: ["🏔️", "🌲", "🏔️", "🌲", "🏔️", "🌲"],
    icon: "🏔️",
    title: "오늘의 라이프맵",
    subtitle: ls("Life Map"),
    color: KR_V2_COLORS.lifeMap,
  },
  relationship: {
    symbols: ["💕", "❤️", "💕", "❤️", "💕", "❤️"],
    icon: "💕",
    title: "관계의 결",
    subtitle: ls("Relationship"),
    color: KR_V2_COLORS.relationship,
  },
  daily_companion: {
    symbols: ["🌅", "☀️", "🌙", "🌤️", "🌇", "🌥️"],
    icon: "🌙",
    title: "오늘의 동행",
    subtitle: ls("Daily Companion"),
    color: KR_V2_COLORS.dailyCompanion,
  },
  compatibility: {
    symbols: ["💎", "✨", "💎", "✨", "💎", "✨"],
    icon: "💎",
    title: "궁합의 흐름",
    subtitle: ls("Compatibility"),
    color: KR_V2_COLORS.compatibility,
  },
  saju: {
    symbols: ["🀄", "☯️", "🀄", "☯️", "🀄", "☯️"],
    icon: "🀄",
    title: "사주팔자",
    subtitle: ls("ASTRIA KOREA · 사주팔자"),
    color: KR_V2_COLORS.saju,
  },
};

// ── Dynamic Tab Background ──
const TabBackground = ({ tabKey }: { tabKey: string }) => {
  const config =
    TAB_CONFIG[tabKey as keyof typeof TAB_CONFIG] || TAB_CONFIG.daily_flow;
  const { symbols, color } = config;

  // Symbol positions - optimized for both mobile and desktop
  const positions = [
    { top: "2%", left: "2%", size: 90, rotate: -12, delay: 0 },
    { top: "12%", right: "5%", size: 70, rotate: 8, delay: 0.4 },
    { bottom: "18%", left: "4%", size: 80, rotate: -5, delay: 0.8 },
    { bottom: "5%", right: "8%", size: 60, rotate: 15, delay: 0.3 },
    { top: "38%", left: "28%", size: 55, rotate: 0, delay: 0.6 },
    { top: "8%", left: "48%", size: 50, rotate: -8, delay: 1.0 },
    { bottom: "32%", right: "22%", size: 65, rotate: 10, delay: 0.5 },
    { top: "52%", right: "10%", size: 45, rotate: -15, delay: 0.7 },
  ];

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
        borderRadius: 24,
        background: `
          ${color.gradient},
          radial-gradient(ellipse at 20% 30%, ${color.primary}10, transparent 60%),
          radial-gradient(ellipse at 80% 70%, ${color.primary}08, transparent 50%)
        `,
      }}
    >
      {/* Floating symbols */}
      {positions.map((pos, idx) => {
        const symbol = symbols[idx % symbols.length];
        return (
          <motion.div
            key={idx}
            initial={{ opacity: 0, scale: 0.3 }}
            animate={{
              opacity: 0.1,
              scale: 1,
              y: [0, -20, 0, 20, 0],
              x: [0, 12, 0, -12, 0],
            }}
            transition={{
              duration: 10 + Math.random() * 4,
              delay: pos.delay,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              position: "absolute",
              top: pos.top,
              left: pos.left,
              right: pos.right,
              bottom: pos.bottom,
              fontSize: pos.size,
              lineHeight: 1,
              opacity: 0.1,
              transform: `rotate(${pos.rotate || 0}deg)`,
              filter: `drop-shadow(0 4px 20px ${color.primary}30)`,
              pointerEvents: "none",
              userSelect: "none",
            }}
          >
            {symbol}
          </motion.div>
        );
      })}

      {/* Floating particles */}
      {Array.from({ length: 12 }).map((_, i) => (
        <motion.div
          key={`p-${i}`}
          initial={{ opacity: 0 }}
          animate={{
            opacity: [0, 0.12, 0],
            y: ["100%", "-20%"],
            x: [`${i * 8}%`, `${i * 8 + 15}%`],
          }}
          transition={{
            duration: 12 + Math.random() * 6,
            delay: i * 0.6,
            repeat: Infinity,
            ease: "linear",
          }}
          style={{
            position: "absolute",
            bottom: "-10%",
            left: `${i * 8 + 2}%`,
            width: 4 + Math.random() * 8,
            height: 4 + Math.random() * 8,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${color.primary}33, transparent)`,
            pointerEvents: "none",
          }}
        />
      ))}
    </div>
  );
};

// ── EXPORTED: Response Container ──
export function KRResponseContainer({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 16,
        maxWidth: 720,
        width: "100%",
        position: "relative",
        zIndex: 2,
      }}
    >
      {children}
    </div>
  );
}

// ── EXPORTED: Section Card ──
export function KRSectionCard({
  title,
  children,
  delay = 0,
  tabKey = "daily_flow",
}: {
  title: string;
  children: React.ReactNode;
  delay?: number;
  tabKey?: string;
}) {
  const config =
    TAB_CONFIG[tabKey as keyof typeof TAB_CONFIG] || TAB_CONFIG.daily_flow;

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      transition={{ delay, duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
      style={{
        position: "relative",
        background: KR_V2_COLORS.cardBg,
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        borderRadius: 16,
        padding: "20px 22px",
        boxShadow: KR_V2_COLORS.shadowSoft,
        border: `1px solid ${config.color.primary}15`,
        overflow: "hidden",
      }}
    >
      {/* Decorative accent */}
      <div
        style={{
          position: "absolute",
          top: -20,
          right: -20,
          width: 80,
          height: 80,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${config.color.primary}06, transparent 70%)`,
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          fontSize: 15,
          fontWeight: 600,
          color: KR_V2_COLORS.textTitle,
          marginBottom: 10,
          letterSpacing: "-0.01em",
          position: "relative",
        }}
      >
        {title}
        <span
          style={{
            position: "absolute",
            bottom: -3,
            left: 0,
            width: 24,
            height: 2,
            background: `linear-gradient(90deg, ${config.color.primary}, ${config.color.primary}44)`,
            borderRadius: 2,
          }}
        />
      </div>

      <div
        style={{
          fontSize: 15,
          fontWeight: 400,
          lineHeight: 1.8,
          color: KR_V2_COLORS.textBody,
          position: "relative",
          zIndex: 1,
        }}
      >
        {children}
      </div>
    </motion.div>
  );
}

// ── Internal Section Card (for main component) ──
const SectionCard = ({
  title,
  children,
  icon,
  tabKey,
  delay = 0,
}: {
  title: string;
  children: React.ReactNode;
  icon?: string;
  tabKey: string;
  delay?: number;
}) => {
  const config =
    TAB_CONFIG[tabKey as keyof typeof TAB_CONFIG] || TAB_CONFIG.daily_flow;

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      transition={{ delay, duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
      style={{
        position: "relative",
        background: KR_V2_COLORS.cardBg,
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        borderRadius: 16,
        padding: "20px 22px",
        boxShadow: KR_V2_COLORS.shadowSoft,
        border: `1px solid ${config.color.primary}15`,
        overflow: "hidden",
      }}
    >
      {/* Decorative accent */}
      <div
        style={{
          position: "absolute",
          top: -20,
          right: -20,
          width: 80,
          height: 80,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${config.color.primary}06, transparent 70%)`,
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10,
        }}
      >
        {icon && (
          <span style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>
            {icon}
          </span>
        )}
        <h3
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: KR_V2_COLORS.textTitle,
            margin: 0,
            letterSpacing: "-0.01em",
            position: "relative",
          }}
        >
          {title}
          <span
            style={{
              position: "absolute",
              bottom: -3,
              left: 0,
              width: 24,
              height: 2,
              background: `linear-gradient(90deg, ${config.color.primary}, ${config.color.primary}44)`,
              borderRadius: 2,
            }}
          />
        </h3>
      </div>

      <div
        style={{
          fontSize: 15,
          fontWeight: 400,
          lineHeight: 1.8,
          color: KR_V2_COLORS.textBody,
          position: "relative",
          zIndex: 1,
        }}
      >
        {children}
      </div>
    </motion.div>
  );
};

// ── Tab Sections ──
const TAB_SECTIONS: Record<
  string,
  {
    key: string;
    title: string;
    kind:
      | "text"
      | "list"
      | "score"
      | "matchPair"
      | "pillars"
      | "elements"
      | "yinyang"
      | "pointList";
    icon: string;
    fields?: { key: string; label: string }[];
  }[]
> = {
  daily_flow: [
    {
      key: "energyMessage",
      title: "에너지 흐름",
      kind: "pointList",
      icon: "🌱",
      fields: [
        { key: "morning", label: "아침" },
        { key: "midday", label: "낮" },
        { key: "evening", label: "저녁" },
      ],
    },
    {
      key: "moodMessage",
      title: "기분의 흐름",
      kind: "pointList",
      icon: "🌸",
      fields: [
        { key: "feeling", label: "기분" },
        { key: "reflection", label: "성찰" },
        { key: "suggestion", label: "제안" },
      ],
    },
    {
      key: "softCheckIn",
      title: "오늘의 마음 체크인",
      kind: "text",
      icon: "💫",
    },
  ],
  life_map: [
    { key: "places", title: "오늘 어울리는 장소", kind: "list", icon: "🏛️" },
    { key: "foods", title: "오늘 어울리는 음식", kind: "list", icon: "🍵" },
    {
      key: "vibeMessage",
      title: "오늘의 전체 분위기",
      kind: "text",
      icon: "🎐",
    },
  ],
  relationship: [
    {
      key: "currentVibe",
      title: "지금 관계의 분위기",
      kind: "text",
      icon: "💕",
    },
    { key: "softAdvice", title: "부드러운 한마디", kind: "text", icon: "🕊️" },
    { key: "tinyAction", title: "오늘의 작은 행동", kind: "text", icon: "🌿" },
  ],
  daily_companion: [
    { key: "morningMessage", title: "아침의 메시지", kind: "text", icon: "🌅" },
    { key: "dayMessage", title: "낮의 기운 흐름", kind: "text", icon: "☀️" },
    { key: "nightMessage", title: "밤의 마음정리", kind: "text", icon: "🌙" },
  ],
  compatibility: [
    { key: "summary", title: "관계 요약", kind: "score", icon: "💎" },
    { key: "energyMatch", title: "에너지 궁합", kind: "matchPair", icon: "⚡" },
    {
      key: "communicationStyle",
      title: "소통 스타일",
      kind: "matchPair",
      icon: "💬",
    },
  ],
  saju: [
    { key: "overview", title: "사주 개요", kind: "text", icon: "🀄" },
    { key: "pillars", title: "년·월·일·시 (四柱)", kind: "pillars", icon: "📜" },
    { key: "pillarReading", title: "사주 풀이", kind: "text", icon: "🖋️" },
    { key: "elements", title: "오행 (五行)", kind: "elements", icon: "🌿" },
    { key: "yinYang", title: "음양 (陰陽)", kind: "yinyang", icon: "☯️" },
    { key: "closing", title: "마무리", kind: "text", icon: "🌙" },
  ],
};

// ── Helpers ──
function getScoreColor(score: number): string {
  if (score >= 80) return "#7BA9A0";
  if (score >= 60) return "#A9C08B";
  if (score >= 40) return "#D9C08A";
  if (score >= 20) return "#D9A98A";
  return "#D99B9B";
}

export function resolveAstriaKoreaV2Tab(
  subCategoryName?: string | null,
): keyof typeof TAB_CONFIG | null {
  if (!subCategoryName) return null;
  const lower = subCategoryName.toLowerCase();
  if (lower.includes("companion talk")) return null;
  if (lower.includes("saju")) return "saju";
  if (lower.includes("daily flow")) return "daily_flow";
  if (lower.includes("life map")) return "life_map";
  if (lower.includes("relationship")) return "relationship";
  if (lower.includes("compatibility") || lower.includes("compatability"))
    return "compatibility";
  if (lower.includes("daily companion") || lower.includes("companion"))
    return "daily_companion";
  return null;
}

// ── Props ──
interface AstriaKoreaV2CardProps {
  content: string;
  subCategoryName?: string | null;
  isStreaming?: boolean;
  structuredData?: any;
  tabType?: string;
  isCompatibilityTab?: boolean;
  onSelectFollowUp?: (question: string) => void;
  timestamp?: string | number | Date;
  onCopy?: () => void;
}

// ── Main Component ──
const AstriaKoreaV2Card: React.FC<AstriaKoreaV2CardProps> = ({
  content,
  subCategoryName,
  isStreaming,
  structuredData,
  tabType,
  onSelectFollowUp,
  timestamp,
  onCopy,
}) => {
  useLnLanguage();
  const resolvedTab =
    tabType || resolveAstriaKoreaV2Tab(subCategoryName) || "daily_flow";
  const config =
    TAB_CONFIG[resolvedTab as keyof typeof TAB_CONFIG] || TAB_CONFIG.daily_flow;
  const sections = TAB_SECTIONS[resolvedTab] || TAB_SECTIONS.daily_flow;

  const followUpQuestions: string[] = useMemo(() => {
    const q = structuredData?.followUpQuestions;
    return Array.isArray(q)
      ? q.filter((s) => typeof s === "string" && s.trim())
      : [];
  }, [structuredData]);

  const renderSection = (section: any, index: number) => {
    const value = structuredData?.[section.key];
    const delay = 0.06 * index;

    if (section.kind === "score") {
      const summaryText = typeof value === "string" ? value : "";
      const rawScore = structuredData?.score;
      const hasScore = typeof rawScore === "number";
      const score = hasScore ? rawScore : 0;
      const color = getScoreColor(score);

      return (
        <SectionCard
          key={section.key}
          title={section.title}
          icon={section.icon}
          tabKey={resolvedTab}
          delay={delay}
        >
          {summaryText && (
            <p
              style={{
                margin: "0 0 16px 0",
                whiteSpace: "pre-wrap",
                lineHeight: 1.8,
              }}
            >
              {summaryText}
            </p>
          )}
          {hasScore && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                background: `${config.color.primary}06`,
                padding: "12px 16px",
                borderRadius: 12,
                border: `1px solid ${config.color.primary}10`,
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  background: `conic-gradient(${color} ${score}%, #E9E3F5 ${score}%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  boxShadow: `0 4px 12px ${color}30`,
                }}
              >
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    background: "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 14,
                    fontWeight: 700,
                    color: KR_V2_COLORS.textTitle,
                  }}
                >
                  {Math.round(score)}
                </div>
              </div>
              <span
                style={{
                  fontSize: 13.5,
                  color: KR_V2_COLORS.textMuted,
                  fontStyle: "italic",
                  flex: 1,
                }}
              >
                {structuredData?.tone || "궁합의 흐름을 살펴보는 중"}
              </span>
            </div>
          )}
        </SectionCard>
      );
    }

    if (section.kind === "pillars") {
      const pillars = value;
      if (
        !pillars ||
        !pillars.yearPillar ||
        !pillars.monthPillar ||
        !pillars.dayPillar ||
        !pillars.hourPillar
      )
        return null;
      const PILLAR_ROWS = [
        { key: "yearPillar", label: "년주", hanja: "年" },
        { key: "monthPillar", label: "월주", hanja: "月" },
        { key: "dayPillar", label: "일주", hanja: "日" },
        { key: "hourPillar", label: "시주", hanja: "時" },
      ];
      return (
        <SectionCard
          key={section.key}
          title={section.title}
          icon={section.icon}
          tabKey={resolvedTab}
          delay={delay}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 8,
            }}
          >
            {PILLAR_ROWS.map((row) => {
              const p = pillars[row.key];
              if (!p) return null;
              return (
                <div
                  key={row.key}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 4,
                    padding: "10px 6px",
                    borderRadius: 10,
                    background: `${config.color.primary}08`,
                    border: `1px solid ${config.color.primary}12`,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      color: KR_V2_COLORS.textMuted,
                    }}
                  >
                    {row.label} ({row.hanja})
                  </span>
                  <span
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: KR_V2_COLORS.textTitle,
                      fontFamily: "'Nanum Myeongjo', 'Noto Serif KR', serif",
                    }}
                  >
                    {p.stem}
                    {p.branch}
                  </span>
                </div>
              );
            })}
          </div>
        </SectionCard>
      );
    }

    if (section.kind === "elements") {
      const elements = value;
      if (!elements) return null;
      const ELEMENT_ROWS = [
        { key: "wood", label: "목 (木)" },
        { key: "fire", label: "화 (火)" },
        { key: "earth", label: "토 (土)" },
        { key: "metal", label: "금 (金)" },
        { key: "water", label: "수 (水)" },
      ];
      const maxCount =
        Math.max(1, ...ELEMENT_ROWS.map((row) => Number(elements[row.key]) || 0));
      return (
        <SectionCard
          key={section.key}
          title={section.title}
          icon={section.icon}
          tabKey={resolvedTab}
          delay={delay}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {ELEMENT_ROWS.map((row) => {
              const count = Number(elements[row.key]) || 0;
              const isDominant = elements.dominant === row.key;
              const isWeak = elements.weak === row.key;
              return (
                <div
                  key={row.key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <span
                    style={{
                      fontSize: 13,
                      width: 56,
                      flexShrink: 0,
                      color: KR_V2_COLORS.textBody,
                      fontWeight: isDominant ? 700 : 400,
                    }}
                  >
                    {row.label}
                  </span>
                  <div
                    style={{
                      flex: 1,
                      height: 8,
                      borderRadius: 4,
                      background: `${config.color.primary}0A`,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${(count / maxCount) * 100}%`,
                        height: "100%",
                        borderRadius: 4,
                        background: isDominant
                          ? config.color.primary
                          : `${config.color.primary}55`,
                      }}
                    />
                  </div>
                  {isDominant && (
                    <span style={{ fontSize: 11, color: config.color.primary }}>
                      강함
                    </span>
                  )}
                  {isWeak && !isDominant && (
                    <span style={{ fontSize: 11, color: KR_V2_COLORS.textMuted }}>
                      약함
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </SectionCard>
      );
    }

    if (section.kind === "yinyang") {
      const yinYang = value;
      if (!yinYang) return null;
      const total = (yinYang.yin || 0) + (yinYang.yang || 0) || 1;
      const yinPct = ((yinYang.yin || 0) / total) * 100;
      const BALANCE_LABEL: Record<string, string> = {
        balanced: "균형",
        "yin-heavy": "음이 강함",
        "yang-heavy": "양이 강함",
      };
      return (
        <SectionCard
          key={section.key}
          title={section.title}
          icon={section.icon}
          tabKey={resolvedTab}
          delay={delay}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div
              style={{
                display: "flex",
                borderRadius: 8,
                overflow: "hidden",
                height: 14,
              }}
            >
              <div
                style={{
                  width: `${yinPct}%`,
                  background: "#5A4F62",
                }}
              />
              <div
                style={{
                  width: `${100 - yinPct}%`,
                  background: "#E8C9A8",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 13,
                color: KR_V2_COLORS.textBody,
              }}
            >
              <span>음(陰) {yinYang.yin}</span>
              <span>양(陽) {yinYang.yang}</span>
            </div>
            <span
              style={{
                fontSize: 12.5,
                color: KR_V2_COLORS.textMuted,
                fontStyle: "italic",
              }}
            >
              {BALANCE_LABEL[yinYang.balance] || yinYang.balance}
            </span>
          </div>
        </SectionCard>
      );
    }

    if (section.kind === "matchPair") {
      const matchData = structuredData?.[section.key];
      if (!matchData || (!matchData.you && !matchData.partner)) return null;
      return (
        <SectionCard
          key={section.key}
          title={section.title}
          icon={section.icon}
          tabKey={resolvedTab}
          delay={delay}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {matchData.you && (
              <p
                style={{
                  margin: 0,
                  padding: "6px 12px",
                  background: `${config.color.primary}08`,
                  borderRadius: 8,
                  fontSize: 14.5,
                }}
              >
                <span
                  style={{ fontWeight: 600, color: KR_V2_COLORS.textTitle }}
                >
                  당신:{" "}
                </span>
                {matchData.you}
              </p>
            )}
            {matchData.partner && (
              <p
                style={{
                  margin: 0,
                  padding: "6px 12px",
                  background: `${config.color.primary}05`,
                  borderRadius: 8,
                  fontSize: 14.5,
                }}
              >
                <span
                  style={{ fontWeight: 600, color: KR_V2_COLORS.textTitle }}
                >
                  상대방:{" "}
                </span>
                {matchData.partner}
              </p>
            )}
          </div>
        </SectionCard>
      );
    }

    if (section.kind === "pointList") {
      // V2's Daily Flow still sends energyMessage/moodMessage as flat
      // strings (unchanged schema); V3's sends them as nested objects
      // (morning/midday/evening, feeling/reflection/suggestion) for
      // bullet-point rendering. Fall back to a plain paragraph for the
      // string shape so V2 content keeps showing instead of silently
      // disappearing when it doesn't match the object shape below.
      if (typeof value === "string" && value.trim()) {
        return (
          <SectionCard
            key={section.key}
            title={section.title}
            icon={section.icon}
            tabKey={resolvedTab}
            delay={delay}
          >
            <p
              style={{
                margin: 0,
                whiteSpace: "pre-wrap",
                lineHeight: 1.8,
                fontSize: 14.5,
              }}
            >
              {value}
            </p>
          </SectionCard>
        );
      }

      const obj = value && typeof value === "object" ? value : null;
      const items = (section.fields || [])
        .map((f: { key: string; label: string }) => ({
          label: f.label,
          text: obj?.[f.key],
        }))
        .filter(
          (item) => typeof item.text === "string" && item.text.trim(),
        );
      if (items.length === 0) return null;
      return (
        <SectionCard
          key={section.key}
          title={section.title}
          icon={section.icon}
          tabKey={resolvedTab}
          delay={delay}
        >
          <ul
            style={{
              margin: 0,
              paddingLeft: 0,
              listStyle: "none",
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            {items.map((item, idx) => (
              <li
                key={idx}
                style={{
                  padding: "4px 0 4px 20px",
                  position: "relative",
                  fontSize: 14.5,
                  lineHeight: 1.8,
                  borderBottom:
                    idx < items.length - 1
                      ? `1px solid ${config.color.primary}08`
                      : "none",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    left: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: config.color.primary,
                    fontSize: 10,
                  }}
                >
                  ◆
                </span>
                <span
                  style={{ fontWeight: 600, color: KR_V2_COLORS.textTitle }}
                >
                  {item.label}:{" "}
                </span>
                {item.text}
              </li>
            ))}
          </ul>
        </SectionCard>
      );
    }

    if (section.kind === "list") {
      const items = Array.isArray(value) ? value.filter(Boolean) : [];
      if (items.length === 0) return null;
      return (
        <SectionCard
          key={section.key}
          title={section.title}
          icon={section.icon}
          tabKey={resolvedTab}
          delay={delay}
        >
          <ul
            style={{
              margin: 0,
              paddingLeft: 0,
              listStyle: "none",
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            {items.map((item, idx) => (
              <li
                key={idx}
                style={{
                  padding: "4px 0 4px 20px",
                  position: "relative",
                  fontSize: 14.5,
                  borderBottom:
                    idx < items.length - 1
                      ? `1px solid ${config.color.primary}08`
                      : "none",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    left: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: config.color.primary,
                    fontSize: 10,
                  }}
                >
                  ◆
                </span>
                {item}
              </li>
            ))}
          </ul>
        </SectionCard>
      );
    }

    // Guard against non-string values (e.g. an object) ever reaching this
    // default text branch — rendering a raw object as a JSX child throws
    // React error #31 instead of failing gracefully.
    const textValue = typeof value === "string" ? value : "";
    if (!textValue) return null;
    return (
      <SectionCard
        key={section.key}
        title={section.title}
        icon={section.icon}
        tabKey={resolvedTab}
        delay={delay}
      >
        <p
          style={{
            margin: 0,
            whiteSpace: "pre-wrap",
            lineHeight: 1.8,
            fontSize: 14.5,
          }}
        >
          {textValue}
        </p>
      </SectionCard>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
      style={{
        position: "relative",
        borderRadius: 24,
        boxShadow: KR_V2_COLORS.shadow,
        padding: "28px 20px 24px",
        maxWidth: 720,
        width: "100%",
        margin: "0 auto",
        fontFamily: KR_FONT,
        overflow: "hidden",
        border: `1px solid ${config.color.primary}12`,
        background: KR_V2_COLORS.bg,
        minHeight: 300,
      }}
    >
      {/* Dynamic Background */}
      <TabBackground tabKey={resolvedTab} />

      {/* Top accent bar */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 20,
          right: 20,
          height: 3,
          background: `linear-gradient(90deg, transparent, ${config.color.primary}, ${config.color.primary}44, transparent)`,
          borderRadius: 3,
          zIndex: 2,
        }}
      />

      {/* Header */}
      <div style={{ marginBottom: 24, position: "relative", zIndex: 2 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <motion.span
            animate={{
              scale: [1, 1.08, 1],
              rotate: [0, 4, -4, 0],
            }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            style={{
              fontSize: 32,
              lineHeight: 1,
              filter: `drop-shadow(0 4px 16px ${config.color.primary}25)`,
              flexShrink: 0,
            }}
          >
            {config.icon}
          </motion.span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: KR_V2_COLORS.textTitle,
                margin: 0,
                lineHeight: 1.3,
                letterSpacing: "-0.02em",
                fontFamily: "'Nanum Myeongjo', 'Noto Serif KR', serif",
              }}
            >
              {config.title}
            </h2>
            <p
              style={{
                fontSize: 12.5,
                color: KR_V2_COLORS.textMuted,
                margin: "2px 0 0 0",
                letterSpacing: "0.02em",
              }}
            >
              {config.subtitle}
            </p>
          </div>
        </div>

        {/* Decorative line */}
        <div
          style={{
            marginTop: 12,
            width: 48,
            height: 2.5,
            background: `linear-gradient(90deg, ${config.color.primary}, ${config.color.primary}30)`,
            borderRadius: 2,
          }}
        />
      </div>

      {/* Content */}
      {structuredData ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            position: "relative",
            zIndex: 2,
          }}
        >
          {sections.map((section, index) => renderSection(section, index))}
        </div>
      ) : (
        <div
          style={{
            position: "relative",
            zIndex: 2,
            background: KR_V2_COLORS.cardBg,
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            borderRadius: 16,
            padding: "20px 22px",
            fontSize: 15,
            lineHeight: 1.8,
            color: KR_V2_COLORS.textBody,
            whiteSpace: "pre-wrap",
            border: `1px solid ${config.color.primary}12`,
            boxShadow: KR_V2_COLORS.shadowSoft,
          }}
        >
          {content || (isStreaming ? "..." : "")}
        </div>
      )}

      {/* Streaming */}
      {isStreaming && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            marginTop: 18,
            display: "flex",
            alignItems: "center",
            gap: 10,
            position: "relative",
            zIndex: 2,
          }}
        >
          <div style={{ display: "flex", gap: 5 }}>
            {[0, 1, 2].map((i) => (
              <motion.span
                key={i}
                animate={{ y: [0, -5, 0], opacity: [0.3, 1, 0.3] }}
                transition={{
                  duration: 0.8,
                  repeat: Infinity,
                  delay: i * 0.15,
                }}
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${config.color.primary}, ${config.color.primary}55)`,
                  display: "inline-block",
                }}
              />
            ))}
          </div>
          <span
            style={{
              fontSize: 12.5,
              color: KR_V2_COLORS.textMuted,
              letterSpacing: "0.03em",
            }}
          >
            생각을 담는 중...
          </span>
        </motion.div>
      )}

      {/* Follow-up */}
      {!isStreaming && followUpQuestions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.35 }}
          style={{
            marginTop: 22,
            paddingTop: 18,
            borderTop: `2px dashed ${config.color.primary}12`,
            position: "relative",
            zIndex: 2,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 12,
              color: KR_V2_COLORS.textMuted,
              marginBottom: 12,
              letterSpacing: "0.02em",
            }}
          >
            <span style={{ fontSize: 14 }}>💭</span>
            이어서 물어보기
            <span
              style={{
                flex: 1,
                height: 1,
                background: `linear-gradient(90deg, ${config.color.primary}20, transparent)`,
              }}
            />
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {followUpQuestions.slice(0, 3).map((q, i) => (
              <motion.button
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.08 * i, duration: 0.25 }}
                type="button"
                onClick={() => onSelectFollowUp?.(q)}
                style={{
                  padding: "8px 16px",
                  borderRadius: 20,
                  background: "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  border: `1px solid ${config.color.primary}18`,
                  color: KR_V2_COLORS.textBody,
                  fontSize: 12.5,
                  fontFamily: KR_FONT,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                }}
                whileHover={{
                  scale: 1.03,
                  background: `${config.color.primary}08`,
                  borderColor: config.color.primary,
                  boxShadow: `0 4px 16px ${config.color.primary}15`,
                }}
                whileTap={{ scale: 0.95 }}
              >
                {q}
              </motion.button>
            ))}
          </div>
        </motion.div>
      )}

      {/* Timestamp + copy — rendered inside the card, not by the outer chat bubble */}
      {(timestamp || onCopy) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: 18,
            paddingTop: 14,
            borderTop: `1px solid ${config.color.primary}12`,
            position: "relative",
            zIndex: 2,
          }}
        >
          <span
            style={{
              fontSize: 12,
              color: KR_V2_COLORS.textMuted,
            }}
          >
            {timestamp
              ? new Date(timestamp).toLocaleTimeString(toLocale(getAppLanguage()), {
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : ""}
          </span>
          {onCopy && (
            <button
              type="button"
              onClick={onCopy}
              title={ls("Copy")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 6,
                borderRadius: 8,
                background: "transparent",
                border: "none",
                color: KR_V2_COLORS.textMuted,
                cursor: "pointer",
                transition: "background 0.2s ease, color 0.2s ease",
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLButtonElement;
                el.style.background = `${config.color.primary}10`;
                el.style.color = config.color.primary;
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLButtonElement;
                el.style.background = "transparent";
                el.style.color = KR_V2_COLORS.textMuted;
              }}
            >
              <Copy size={13} />
            </button>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default AstriaKoreaV2Card;
